import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { paginatedSchema } from "../pagination";

import { useBootstrap } from "./bootstrap";

export const userListItemSchema = z.object({
  url: z.string(),
  id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  username: z.string(),
  email: z.string(),
});

export type UserListItem = z.infer<typeof userListItemSchema>;

export const userListSchema = paginatedSchema(userListItemSchema);
export type UserList = z.infer<typeof userListSchema>;

export const userSchema = userListItemSchema;
export type User = z.infer<typeof userSchema>;

export function useUsers(params = {}) {
  const url = combineUrlAndParams("/api/v4/users/", params);
  return useParsedQuery<UserList>(
    ["lizard-api-v4", "users", url],
    () => fetchWithError(url),
    (json: unknown) => userListSchema.parse(json)
  );
}

export function useUser(id: number) {
  return useParsedQuery<User>(
    ["lizard-api-v4", "users", id],
    () => fetchWithError(`/api/v4/users/${id}/`),
    (json: unknown) => userSchema.parse(json)
  );
}

export function useUserMe() {
  // Only use this if a user is authenticated
  // If user isn't, this will remain 'loading' or 'idle'.
  const bootstrap = useBootstrap();
  const isAuthenticated = bootstrap.isSuccess && bootstrap.data.user.authenticated;

  return useParsedQuery<User>(
    ["lizard-api-v4", "users", "me"],
    () => fetchWithError("/api/v4/users/me/"),
    (json: unknown) => userSchema.parse(json),
    {
      enabled: isAuthenticated,
    }
  );
}

export function useMultipleUsers(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "users", id],
        fetchFunction: () => fetchWithError(`/api/v4/users/${id}/`),
        parseFunction: (json: unknown) => userSchema.parse(json),
      };
    })
  );
}

export const userOrganisationsSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  name: z.string(),
  roles: z.array(z.string()),
});

export type UserOrganisations = z.infer<typeof userOrganisationsSchema>;

export function useUserOrganisations(userId: number | null) {
  return useParsedQuery<UserOrganisations[]>(
    ["lizard-api-v4", "users", userId, "organisation"],
    () =>
      userId !== null
        ? fetchWithError(`/api/v4/users/${userId}/organisations/?page_size=0`)
        : Promise.resolve(null),
    (json: unknown) => z.array(userOrganisationsSchema).parse(json),
    { enabled: userId !== null }
  );
}
